import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef } from "react";
import type { PortfolioRow } from "../components/portfolio/columns/columnTypes";
import type { PortfolioTableConfig } from "../components/portfolio/configs/types";
import { contractsService } from "../services/contractsService";
import marketDataService from "../services/marketDataService";
import { optionChainsV2Service } from "../services/optionChainsV2Service";
import type { OptionPriceResult, StockPriceResult } from "../types/marketData";
import type { IbkrPositionV2 } from "../types";
import { fxRatesQueryKeys } from "./useFxRates";
import { portfolioQueryKeys } from "./usePortfolioData";
import {
  isOptionPosition,
  isUsableFxRate,
  isUsableOptionCoverage,
  isUsableStockQuote,
  positionToRow,
} from "./portfolioDataCore";
import {
  type AdminCustomerViewAccountQueryOptions,
  type AdminCustomerViewAccountSummary,
  useAdminCustomerViewAccountSummary,
  useAdminCustomerViewPositions,
  adminCustomerViewAccountQueryKeys,
} from "./useAdminCustomerViewAccountData";
import { isValidPlutusUserId } from "../utils/plutusUserId";

export type TargetSupportingResourceState = Readonly<{
  isLoading: boolean;
  error: Error | null;
  complete: boolean;
}>;

const IDLE_SUPPORT: TargetSupportingResourceState = {
  isLoading: false,
  error: null,
  complete: false,
};

const COMPLETE_SUPPORT: TargetSupportingResourceState = {
  isLoading: false,
  error: null,
  complete: true,
};

function normalizeSupportingError(value: unknown): Error | null {
  if (value === null || value === undefined) return null;
  return value instanceof Error ? value : new Error("Supporting data request failed.");
}

export interface AdminCustomerViewPortfolioDataParams {
  targetUserId: string;
  config: PortfolioTableConfig;
  enabled: boolean;
  accountMaxAgeMs: number;
  accountQueryOptions: AdminCustomerViewAccountQueryOptions;
}

export interface AdminCustomerViewPortfolioDataResult {
  rows: PortfolioRow[];
  accountSummary: AdminCustomerViewAccountSummary | null;
  positionsLoading: boolean;
  summaryLoading: boolean;
  positionsError: Error | null;
  summaryError: Error | null;
  positionsLoadedForUserId: string | null;
  summaryLoadedForUserId: string | null;
  positionsDataUpdatedAt: number;
  summaryDataUpdatedAt: number;
  supportingData: Readonly<{
    marketPrices: TargetSupportingResourceState;
    contractDetails: TargetSupportingResourceState;
    tradableTimes: TargetSupportingResourceState;
    fxRates: TargetSupportingResourceState;
  }>;
  lastUpdatedAt: number;
  refreshAccount: () => Promise<void>;
  retrySupportingData: (
    resource: "marketPrices" | "contractDetails" | "tradableTimes" | "fxRates"
  ) => Promise<void>;
}

function emptyResult(): AdminCustomerViewPortfolioDataResult {
  return {
    rows: [],
    accountSummary: null,
    positionsLoading: false,
    summaryLoading: false,
    positionsError: null,
    summaryError: null,
    positionsLoadedForUserId: null,
    summaryLoadedForUserId: null,
    positionsDataUpdatedAt: 0,
    summaryDataUpdatedAt: 0,
    supportingData: {
      marketPrices: IDLE_SUPPORT,
      contractDetails: IDLE_SUPPORT,
      tradableTimes: IDLE_SUPPORT,
      fxRates: IDLE_SUPPORT,
    },
    lastUpdatedAt: 0,
    refreshAccount: async () => undefined,
    retrySupportingData: async () => undefined,
  };
}

export function useAdminCustomerViewPortfolioData(
  params: AdminCustomerViewPortfolioDataParams
): AdminCustomerViewPortfolioDataResult {
  const queryClient = useQueryClient();
  const refreshAccountFlightRef = useRef<Promise<void> | null>(null);
  const accountEnabled = params.enabled === true && isValidPlutusUserId(params.targetUserId);

  const positions = useAdminCustomerViewPositions({
    targetUserId: params.targetUserId,
    enabled: accountEnabled,
    maxAgeMs: params.accountMaxAgeMs,
    queryOptions: params.accountQueryOptions,
  });
  const summary = useAdminCustomerViewAccountSummary({
    targetUserId: params.targetUserId,
    enabled: accountEnabled,
    maxAgeMs: params.accountMaxAgeMs,
    queryOptions: params.accountQueryOptions,
  });

  const positionsOwned = positions.error === null
    && positions.loadedForUserId === params.targetUserId
    && positions.data !== undefined;
  const supportEnabled = accountEnabled && positionsOwned;
  const activePositions = useMemo(() => {
    if (!positionsOwned || !positions.data) return [] as IbkrPositionV2[];
    return positions.data.filter((row) => Number(row.position) !== 0);
  }, [positionsOwned, positions.data]);

  const conIds = useMemo(
    () => Array.from(new Set(activePositions.map((row) => row.conId))),
    [activePositions]
  );
  const optionConIds = useMemo(
    () => Array.from(new Set(
      activePositions.filter((row) => isOptionPosition(row.secType)).map((row) => row.conId)
    )),
    [activePositions]
  );
  const stockConIds = useMemo(
    () => conIds.filter((id) => !optionConIds.includes(id)),
    [conIds, optionConIds]
  );

  const stockQuery = useQuery({
    queryKey: portfolioQueryKeys.batchPrices(stockConIds),
    queryFn: () => marketDataService.getStockPricesChunked(stockConIds, {
      chunkSize: 50,
      maxConcurrent: 3,
      retryAttempts: 2,
    }),
    enabled: supportEnabled && stockConIds.length > 0,
    staleTime: 30 * 1000,
    retry: false,
  });
  const optionPriceQuery = useQuery({
    queryKey: portfolioQueryKeys.optionPrices(optionConIds),
    queryFn: (): Promise<Record<number, OptionPriceResult>> =>
      marketDataService.getOptionPrices(optionConIds),
    enabled: supportEnabled && optionConIds.length > 0,
    staleTime: 30 * 1000,
    retry: false,
  });
  const optionMultiplierQuery = useQuery({
    queryKey: portfolioQueryKeys.optionMultipliers(optionConIds),
    queryFn: async (): Promise<Record<number, number>> => {
      const res = await optionChainsV2Service.findOptionContractsByConIds(optionConIds);
      const map: Record<number, number> = {};
      for (const contract of res.optionContracts) {
        map[contract.conId] = Number(contract.multiplier) || 0;
      }
      return map;
    },
    enabled: supportEnabled && optionConIds.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const contractQuery = useQuery({
    queryKey: portfolioQueryKeys.contractDetails(conIds),
    queryFn: () => contractsService.getContractDetailsMap(conIds),
    enabled: supportEnabled && conIds.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const tradableQuery = useQuery({
    queryKey: portfolioQueryKeys.tradableTimes(conIds),
    queryFn: () => contractsService.getTimeZoneMap(conIds),
    enabled: supportEnabled && params.config.dataRequirements.tradableTimes && conIds.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const contractDetailsMap = useMemo(
    () => contractQuery.error === null ? (contractQuery.data ?? {}) : {},
    [contractQuery.data, contractQuery.error]
  );
  const rawPrices = useMemo<Record<number, StockPriceResult>>(
    () => stockQuery.error === null ? (stockQuery.data ?? {}) : {},
    [stockQuery.data, stockQuery.error]
  );
  const optionPrices = useMemo<Record<number, OptionPriceResult>>(
    () => optionPriceQuery.error === null ? (optionPriceQuery.data ?? {}) : {},
    [optionPriceQuery.data, optionPriceQuery.error]
  );
  const optionMultipliers = useMemo<Record<number, number>>(
    () => optionMultiplierQuery.error === null ? (optionMultiplierQuery.data ?? {}) : {},
    [optionMultiplierQuery.data, optionMultiplierQuery.error]
  );
  const tradableTimes = useMemo(
    () => tradableQuery.error === null ? tradableQuery.data : undefined,
    [tradableQuery.data, tradableQuery.error]
  );
  const effectiveBatchPrices = useMemo((): Record<number, { price: number; currency: string }> => {
    const merged: Record<number, { price: number; currency: string }> = {};
    for (const [conIdStr, priceData] of Object.entries(rawPrices)) {
      if (!isUsableStockQuote(priceData)) continue;
      const conId = Number(conIdStr);
      const currency =
        activePositions.find((row) => row.conId === conId)?.currency
        ?? contractDetailsMap[conId]?.currency
        ?? "USD";
      merged[conId] = { price: priceData.price, currency };
    }
    return merged;
  }, [rawPrices, contractDetailsMap, activePositions]);

  const currencies = useMemo(() => {
    const positionCurrencies = activePositions
      .map((row) => row.currency || contractDetailsMap[row.conId]?.currency || "")
      .filter((currency) => currency.length > 0);
    const priceCurrencies = Object.values(effectiveBatchPrices)
      .map((row) => row.currency)
      .filter((currency) => currency.length > 0);
    return Array.from(new Set([...positionCurrencies, ...priceCurrencies]));
  }, [activePositions, contractDetailsMap, effectiveBatchPrices]);
  const uniqueNonUsd = useMemo(
    () => Array.from(new Set(currencies.map((c) => c.toUpperCase())))
      .filter((c) => c.length > 0 && c !== "USD"),
    [currencies]
  );

  const fxQuery = useQuery({
    queryKey: fxRatesQueryKeys.rates(uniqueNonUsd),
    queryFn: () => marketDataService.getForexRates(uniqueNonUsd),
    enabled: supportEnabled && uniqueNonUsd.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const fxRates = useMemo(() => {
    const rates: Record<string, number> = { USD: 1.0 };
    if (fxQuery.error === null && fxQuery.data) {
      for (const [currency, entry] of Object.entries(fxQuery.data)) {
        rates[currency.toUpperCase()] = entry.rate;
      }
    }
    return rates;
  }, [fxQuery.data, fxQuery.error]);

  const summaryOwned = summary.error === null
    && summary.loadedForUserId === params.targetUserId
    && summary.data !== undefined;
  const netLiq = summaryOwned ? summary.data!.netLiquidationSecurity : undefined;

  const rows = useMemo(() => {
    if (!positionsOwned) return [];
    return activePositions.map((pos) => positionToRow(
      pos,
      effectiveBatchPrices[pos.conId],
      netLiq,
      tradableTimes,
      fxRates,
      contractDetailsMap[pos.conId]?.minSize,
      isOptionPosition(pos.secType)
        ? {
          quote: optionPrices[pos.conId],
          multiplier: optionMultipliers[pos.conId],
        }
        : undefined,
      contractDetailsMap[pos.conId]?.currency ?? "USD"
    ));
  }, [
    positionsOwned,
    activePositions,
    effectiveBatchPrices,
    netLiq,
    tradableTimes,
    fxRates,
    contractDetailsMap,
    optionPrices,
    optionMultipliers,
  ]);

  const supportingData = useMemo(() => {
    if (!supportEnabled) {
      return {
        marketPrices: IDLE_SUPPORT,
        contractDetails: IDLE_SUPPORT,
        tradableTimes: IDLE_SUPPORT,
        fxRates: IDLE_SUPPORT,
      };
    }
    if (activePositions.length === 0) {
      return {
        marketPrices: COMPLETE_SUPPORT,
        contractDetails: COMPLETE_SUPPORT,
        tradableTimes: COMPLETE_SUPPORT,
        fxRates: COMPLETE_SUPPORT,
      };
    }

    const stockComplete = stockConIds.every((id) => isUsableStockQuote(rawPrices[id]));
    const optionComplete = optionConIds.every((id) => {
      const pos = activePositions.find((row) => row.conId === id);
      return isUsableOptionCoverage(
        optionPrices[id],
        optionMultipliers[id],
        Number(pos?.position) || 0
      );
    });
    const marketLoading = (stockConIds.length > 0 && stockQuery.isFetching)
      || (optionConIds.length > 0 && (optionPriceQuery.isFetching || optionMultiplierQuery.isFetching));
    const stockError = normalizeSupportingError(stockQuery.error);
    const optionPriceError = normalizeSupportingError(optionPriceQuery.error);
    const optionMultiplierError = normalizeSupportingError(optionMultiplierQuery.error);
    const marketError = stockError ?? optionPriceError ?? optionMultiplierError;
    const contractError = normalizeSupportingError(contractQuery.error);
    const tradableError = normalizeSupportingError(tradableQuery.error);
    const fxError = normalizeSupportingError(fxQuery.error);

    const detailsComplete = conIds.every((id) => Boolean(contractDetailsMap[id]));
    const tradableRequired = params.config.dataRequirements.tradableTimes;
    const tradableComplete = !tradableRequired || conIds.every((id) => Boolean(tradableTimes?.[id]));
    const fxComplete = uniqueNonUsd.every((code) => isUsableFxRate(fxRates[code]));

    return {
      marketPrices: {
        isLoading: marketLoading,
        error: marketError,
        complete: !marketLoading && marketError === null && stockComplete && optionComplete,
      },
      contractDetails: {
        isLoading: contractQuery.isFetching,
        error: contractError,
        complete: !contractQuery.isFetching && contractError === null && detailsComplete,
      },
      tradableTimes: {
        isLoading: tradableRequired && tradableQuery.isFetching,
        error: tradableError,
        complete: !tradableRequired
          || (!tradableQuery.isFetching && tradableError === null && tradableComplete),
      },
      fxRates: {
        isLoading: uniqueNonUsd.length > 0 && fxQuery.isFetching,
        error: fxError,
        complete: uniqueNonUsd.length === 0
          || (!fxQuery.isFetching && fxError === null && fxComplete),
      },
    };
  }, [
    supportEnabled,
    activePositions,
    stockConIds,
    optionConIds,
    rawPrices,
    optionPrices,
    optionPriceQuery.isFetching,
    optionPriceQuery.error,
    optionMultipliers,
    optionMultiplierQuery.isFetching,
    optionMultiplierQuery.error,
    stockQuery.isFetching,
    stockQuery.error,
    conIds,
    contractDetailsMap,
    contractQuery.isFetching,
    contractQuery.error,
    params.config.dataRequirements.tradableTimes,
    tradableTimes,
    tradableQuery.isFetching,
    tradableQuery.error,
    uniqueNonUsd,
    fxRates,
    fxQuery.isFetching,
    fxQuery.error,
  ]);

  const refreshAccount = useCallback((): Promise<void> => {
    if (!accountEnabled) return Promise.resolve();
    if (refreshAccountFlightRef.current) {
      return refreshAccountFlightRef.current;
    }
    const request = Promise.all([
      queryClient.refetchQueries({
        queryKey: adminCustomerViewAccountQueryKeys.positions(params.targetUserId),
        exact: true,
        type: "active",
      }, { cancelRefetch: false }),
      queryClient.refetchQueries({
        queryKey: adminCustomerViewAccountQueryKeys.summary(params.targetUserId),
        exact: true,
        type: "active",
      }, { cancelRefetch: false }),
    ]).then(() => undefined);
    refreshAccountFlightRef.current = request;
    const clear = () => {
      if (refreshAccountFlightRef.current === request) {
        refreshAccountFlightRef.current = null;
      }
    };
    void request.then(clear, clear);
    return request;
  }, [accountEnabled, params.targetUserId, queryClient]);

  const retrySupportingData = useCallback(async (
    resource: "marketPrices" | "contractDetails" | "tradableTimes" | "fxRates"
  ) => {
    if (!supportEnabled) return;
    const opts = { throwOnError: false };
    if (resource === "marketPrices") {
      const tasks: Promise<unknown>[] = [];
      if (stockConIds.length > 0) {
        tasks.push(queryClient.refetchQueries({
          queryKey: portfolioQueryKeys.batchPrices(stockConIds),
          exact: true,
        }, opts));
      }
      if (optionConIds.length > 0) {
        tasks.push(
          queryClient.refetchQueries({
            queryKey: portfolioQueryKeys.optionPrices(optionConIds),
            exact: true,
          }, opts),
          queryClient.refetchQueries({
            queryKey: portfolioQueryKeys.optionMultipliers(optionConIds),
            exact: true,
          }, opts),
        );
      }
      await Promise.all(tasks);
      return;
    }
    if (resource === "contractDetails" && conIds.length > 0) {
      await queryClient.refetchQueries({
        queryKey: portfolioQueryKeys.contractDetails(conIds),
        exact: true,
      }, opts);
      return;
    }
    if (resource === "tradableTimes" && conIds.length > 0) {
      await queryClient.refetchQueries({
        queryKey: portfolioQueryKeys.tradableTimes(conIds),
        exact: true,
      }, opts);
      return;
    }
    if (resource === "fxRates" && uniqueNonUsd.length > 0) {
      await queryClient.refetchQueries({
        queryKey: fxRatesQueryKeys.rates(uniqueNonUsd),
        exact: true,
      }, opts);
    }
  }, [
    supportEnabled,
    stockConIds,
    optionConIds,
    conIds,
    uniqueNonUsd,
    queryClient,
  ]);

  if (!accountEnabled) {
    return {
      ...emptyResult(),
      summaryError: null,
      positionsError: null,
      refreshAccount,
      retrySupportingData,
    };
  }

  return {
    rows,
    accountSummary: summaryOwned ? summary.data ?? null : null,
    positionsLoading: positions.isLoading,
    summaryLoading: summary.isLoading,
    positionsError: positions.error,
    summaryError: summary.error,
    positionsLoadedForUserId: positions.loadedForUserId,
    summaryLoadedForUserId: summary.loadedForUserId,
    positionsDataUpdatedAt: positions.dataUpdatedAt,
    summaryDataUpdatedAt: summary.dataUpdatedAt,
    supportingData,
    lastUpdatedAt: Math.max(positions.dataUpdatedAt, summary.dataUpdatedAt),
    refreshAccount,
    retrySupportingData,
  };
}
